<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { JwtContent } from '@/types'
import { useIsMobile } from '@/composables/useIsMobile'
import { useTypedRoute } from '@/composables'

const { t } = useI18n()

const props = defineProps<{
  jwt: JwtContent
}>()

const emits = defineEmits<{
  (e: 'logout', logout: boolean): void
}>()

const route = useTypedRoute<{ id: string }>()
const menu = ref(false)
const { isMobile } = useIsMobile(700)

const getInitials = (): string => {
  if (props.jwt.surname && props.jwt.name) {
    return `${props.jwt.name[0]}${props.jwt.surname[0]}`.toUpperCase()
  }
  if (props.jwt.name) {
    return props.jwt.name.substring(0, 2).toUpperCase()
  }
  if (props.jwt.email) {
    return props.jwt.email.substring(0, 2).toUpperCase()
  }
  return '?'
}

const displayName = computed(() => {
  if (props.jwt.surname && props.jwt.name) {
    return `${props.jwt.name} ${props.jwt.surname}`
  }
  if (props.jwt.name) {
    return props.jwt.name
  }
  if (props.jwt.email) {
    return props.jwt.email.split('@')[0]
  }
  return 'Account'
})

const roleClass = computed(() => {
  switch (props.jwt.role) {
    case 'admin': return 'role-admin'
    case 'reseller': return 'role-reseller'
    default: return 'role-customer'
  }
})

const roleLabel = computed(() => {
  switch (props.jwt.role) {
    case 'admin': return t('dashboard.nav.roles.admin')
    case 'reseller': return t('dashboard.nav.roles.reseller')
    default: return t('dashboard.nav.roles.customer')
  }
})

const handleLogout = () => {
  emits('logout', true)
}
</script>

<template>
  <div class="account-menu-wrapper">
    <!-- Desktop: Pill-style button -->
    <button
      v-if="!isMobile"
      class="user-pill"
      :class="{ active: menu }"
      :aria-expanded="menu"
      aria-haspopup="true"
      @click="menu = !menu"
    >
      <v-icon icon="mdi-account" size="18" />
      <span class="user-name">{{ displayName }}</span>
      <v-icon
        :icon="menu ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        size="18"
        class="chevron"
      />
    </button>

    <!-- Mobile: Simple icon button -->
    <button
      v-else
      class="user-pill mobile"
      :class="{ active: menu }"
      @click="menu = !menu"
    >
      <v-icon icon="mdi-account" size="20" />
    </button>

    <!-- Dropdown Menu -->
    <Transition name="dropdown">
      <div v-if="menu" class="dropdown-overlay" @click="menu = false">
        <div class="dropdown-menu" @click.stop>
          <!-- User Header -->
          <div class="dropdown-header">
            <div class="user-avatar">
              <span>{{ getInitials() }}</span>
            </div>
            <div class="user-info">
              <span class="user-display-name">{{ displayName }}</span>
              <span class="user-role" :class="roleClass">{{ roleLabel }}</span>
            </div>
          </div>

          <div class="dropdown-divider"></div>

          <!-- Menu Items -->
          <router-link
            :to="{ name: route.path.includes('dashboard') ? 'landingPage' : 'dashboardOverview' }"
            class="dropdown-item"
            @click="menu = false"
          >
            <v-icon :icon="route.path.includes('dashboard') ? 'mdi-home' : 'mdi-view-dashboard'" size="18" />
            <span>{{ route.path.includes('dashboard') ? 'Home' : 'Dashboard' }}</span>
          </router-link>

          <router-link
            :to="{ name: 'dashboardAccount', params: { tab: 'details' } }"
            class="dropdown-item"
            @click="menu = false"
          >
            <v-icon icon="mdi-account-circle" size="18" />
            <span>Account</span>
          </router-link>

          <div class="dropdown-divider"></div>

          <button class="dropdown-item logout" @click="handleLogout">
            <v-icon icon="mdi-logout" size="18" />
            <span>{{ $t('auth.logout') }}</span>
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.account-menu-wrapper {
  position: relative;
}

.user-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  .v-icon {
    opacity: 0.7;
  }

  .chevron {
    opacity: 0.5;
    margin-left: 2px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(120, 163, 255, 0.25);
    color: rgba(255, 255, 255, 0.95);

    .v-icon {
      opacity: 0.9;
    }
  }

  &.active {
    background: rgba(0, 176, 255, 0.12);
    border-color: rgba(0, 176, 255, 0.3);
    color: #00B0FF;

    .v-icon {
      opacity: 1;
      color: #00B0FF;
    }
  }

  &.mobile {
    padding: 10px;

    .user-name,
    .chevron {
      display: none;
    }
  }
}

.user-name {
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Dropdown */
.dropdown-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  background: rgba(15, 23, 48, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 14px;
  padding: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  z-index: 1001;
}

.dropdown-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
}

.user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(0, 176, 255, 0.2), rgba(31, 226, 212, 0.15));
  border: 1px solid rgba(0, 176, 255, 0.3);
  border-radius: 10px;
  flex-shrink: 0;

  span {
    font-size: 0.85rem;
    font-weight: 600;
    color: #00B0FF;
    text-transform: uppercase;
  }
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.user-display-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-size: 0.7rem;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
  width: fit-content;

  &.role-admin {
    background: rgba(244, 67, 54, 0.15);
    border: 1px solid rgba(244, 67, 54, 0.25);
    color: #F44336;
  }

  &.role-reseller {
    background: rgba(255, 152, 0, 0.15);
    border: 1px solid rgba(255, 152, 0, 0.25);
    color: #FF9800;
  }

  &.role-customer {
    background: rgba(33, 150, 243, 0.15);
    border: 1px solid rgba(33, 150, 243, 0.25);
    color: #2196F3;
  }
}

.dropdown-divider {
  height: 1px;
  background: rgba(120, 163, 255, 0.1);
  margin: 6px 8px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.9rem;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.15s ease;
  width: 100%;

  .v-icon {
    opacity: 0.6;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.95);

    .v-icon {
      opacity: 0.9;
    }
  }

  &.logout {
    color: rgba(244, 67, 54, 0.8);

    .v-icon {
      color: rgba(244, 67, 54, 0.7);
    }

    &:hover {
      background: rgba(244, 67, 54, 0.1);
      color: #F44336;

      .v-icon {
        color: #F44336;
        opacity: 1;
      }
    }
  }
}

/* Dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;

  .dropdown-menu {
    transform: translateY(-8px) scale(0.95);
  }
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;

  .dropdown-menu {
    transform: translateY(0) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .user-pill,
  .dropdown-item {
    transition: none;
  }

  .dropdown-enter-active,
  .dropdown-leave-active {
    transition: none;
  }
}
</style>
